import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { BasketItem } from '../../../../types/basket';
import { useCheckoutState } from '../../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../../utils/config';
import FormattedCurrency from '../../../atoms/FormattedCurrency';
import Price from '../../../atoms/Price';
import { showDefaultError } from '../../Notifications';
import Quantity from '../Quantity';
import './style.scss';

type ProductDetailsProps = {
  item: BasketItem;
  href: string;
};

const ProductDetails = ({ item, href }: ProductDetailsProps) => {
  const { removeBasketItem, setBasketEmpty } = useCheckoutState();
  const [isRemoving, setRemoving] = useState(false);

  const { sale, campaign, voucher } = item.discount;
  const isDiscounted = !!sale || !!campaign || !!voucher;

  const onRemoveItem = async () => {
    setRemoving(true);
    try {
      await removeBasketItem({ id: item.id });
    } catch (e: any) {
      if (e.statusCode === 204) {
        setBasketEmpty(true);
        return;
      }
      console.error(e);
      showDefaultError();
    }
    setRemoving(false);
  };

  const showLowestPriorPrice =
    config.features.lowestPriorPrice.enabled && !!item.discount.total && !!item.lowestPriorPrice;

  return (
    <div className="product-details">
      <button
        className="product-details__remove"
        type="button"
        onClick={onRemoveItem}
        disabled={isRemoving}
        data-test-id="item-remove"
      >
        <img className="product-details__remove-icon" src={getAssetUrl('common/remove.svg')} />
      </button>
      <a
        className="product-details__section product-details__section--name-and-attributes"
        href={href}
        onClick={!IS_WEB_COMPONENT ? undefined : () => (window.location.href = href)}
      >
        <span className="product-details__brand" data-test-id="item-brand">
          {item.brand.name}
        </span>
        <p className="product-details__title" data-test-id="item-name">
          {item.name}
        </p>
        {item.displayData.attributes && (
          <ul className="product-details__attributes">
            {item.displayData.attributes.map(attribute => (
              <li key={attribute.label.translationKey ?? attribute.label.clientProvided}>
                {attribute.label.translationKey ? (
                  <FormattedMessage id={`basket.item.attribute.${attribute.label.translationKey}`} />
                ) : (
                  attribute.label.clientProvided
                )}
                : {attribute.value}
              </li>
            ))}
          </ul>
        )}
      </a>
      <div className="product-details__section product-details__section--quantity-and-price">
        <Quantity item={item} />
        <div className="product-details__price">
          {isDiscounted && <Price price={item.total.costWithTaxWithoutDiscount} isInactive />}
          <Price price={item.total.costWithTaxWithDiscount} isDiscounted={isDiscounted} />
        </div>
      </div>
      {showLowestPriorPrice && (
        <p className="product-details__lowest-prior-price">
          <FormattedMessage id="basket.item.lowestPriorPrice" />:
          <span className="product-details__lowest-prior-value">
            <FormattedCurrency value={item.lowestPriorPrice!.withTax / 100} />
          </span>
        </p>
      )}
    </div>
  );
};

export default ProductDetails;
